import React, { useState } from "react";
import Accordion from "../../../../../globalComponents/ui/Accordion";
import { AvatarGroup } from "../../../../../globalComponents/ui/Avatar";
import { StatusBadge } from "../../../../../globalComponents/ui/Badge";
import DataCell from "../../../../../globalComponents/ui/Cell";
import { HighlightText } from "../KpiIcons";
import InfoTip from "../../../../../globalComponents/ui/InfoTip";
import { formatNumber } from "../../../../../utils/numberFormat";
import { useKpiRowData } from "./useKpiRowData";
import KpiRowOverview from "./KpiRowOverview";
import SubQuestionsModal from "./SubQuestionsModal";
import TriggerBadge from "./TriggerBadge";
import LockBadge from "./LockBadge";
import { OPEN } from "../../../KpiLock/helpers";
import { CheckboxCell, RowNumberCell, KpiNameCell, KpiNameText } from "./KpiRow.styled";

/** A KPI with no deadline configured, so nothing to lock. */
const NO_LOCK = { status: OPEN, dueDate: null, lockedLocationIds: [] };

/**
 * KpiRow — slim orchestrator that composes global UI components.
 * All business logic lives in useKpiRowData hook.
 */
const KpiRow = ({
  item,
  index = 0,
  selected,
  onToggle,
  searchQuery = "",
  expanded = false,
  onExpand,
  onAdd,
  onChat,
  onAudit,
  onAssign,
  onHistory,
  isEven = false,
  emissionMode = false,
  hasPermission,
  lock = NO_LOCK,
  accessRequested = false,
  accessDeclined = false,
  requestingAccess = false,
  onRequestAccess,
  canDecideAccess = false,
  pendingRequestIds = [],
  pendingRequestLocationIds = [],
  onApproveAccess,
  onRejectAccess,
}) => {
  const [showSubQModal, setShowSubQModal] = useState(false);
  const data = useKpiRowData(item);
  const {
    status, matchingTriggers, triggersFallback, projection,
    normalizedAuditors, analytics, kpiTypes, canAddAndEditRecords,
  } = data;

  const subQuestions = Array.isArray(item.subQuestions) ? item.subQuestions : [];
  const isDerived = Boolean(item.isFormulaBased);

  const navigateToSubQuestion = (subId) => {
    setShowSubQModal(false);
    setTimeout(() => {
      const el = document.querySelector(`[data-kpi-id="${subId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.style.outline = "2px solid #2563eb";
        setTimeout(() => { el.style.outline = ""; }, 1500);
      }
    }, 150);
  };

  const baseBg = isEven ? "#ffffff" : "#f8fafd";
  const derivedBg = "#eff6ff";
  const rowBg = isDerived
    ? (expanded ? "#dbeafe" : derivedBg)
    : (expanded ? "#eef4ff" : selected ? "#e6eeff" : baseBg);
  const rowShadow = isDerived
    ? "inset 3px 0 0 #2563eb"
    : expanded ? "inset 3px 0 0 var(--accent)" : "none";

  /* ── Value formatting ── */
  const valueDisplay = emissionMode
    ? (item.fy_emission != null ? `${formatNumber(item.fy_emission)}${item.emission_unit ? ` ${item.emission_unit}` : ""}` : null)
    : (item.value != null ? `${formatNumber(item.value)}${item.unit ? ` ${item.unit}` : ""}` : null);

  const draftDisplay = !emissionMode && Number(item.analytics?.fy_value_draft) > 0
    ? `${formatNumber(item.analytics.fy_value_draft)}${item.unit ? ` ${item.unit}` : ""}`
    : null;

  const header = (
    <>
      {/* Checkbox */}
      <CheckboxCell onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" className="kpi-checkbox" checked={selected} onChange={onToggle} disabled={emissionMode} />
      </CheckboxCell>

      {/* Row number */}
      <RowNumberCell>{index + 1}.</RowNumberCell>

      {/* KPI name */}
      <KpiNameCell>
        <KpiNameText $isDerived={isDerived}>
          <HighlightText text={item.kpi_name} query={searchQuery} />
          <InfoTip text={item.description} />
        </KpiNameText>
        <LockBadge lock={lock} />
        {matchingTriggers.length > 0 && (
          <TriggerBadge triggers={matchingTriggers} isFallback={triggersFallback} projection={projection} />
        )}
      </KpiNameCell>

      {/* Assigned To / By / Auditors */}
      <AvatarGroup users={item.assigned_to} width={150} emptyLabel="Unassigned" />
      <AvatarGroup users={item.assigned_by} width={130} emptyLabel="Unassigned" />
      <AvatarGroup
        users={item.auditorsError || item.auditors === null ? null : normalizedAuditors}
        width={150}
        emptyLabel={item.auditorsError || item.auditors === null ? "Auditor data unavailable" : "No auditor"}
        isError={Boolean(item.auditorsError || item.auditors === null)}
      />

      {/* Status */}
      <DataCell width={120}>
        <StatusBadge text={status.text} color={status.color} bg={status.bg} />
      </DataCell>

      {/* Value */}
      <DataCell width={120} value={valueDisplay} />

      {/* Draft (non-emission only) */}
      {!emissionMode && (
        <DataCell width={120} value={draftDisplay} variant="draft" title="Sum of draft records — not yet submitted" />
      )}
    </>
  );

  return (
    <>
      {showSubQModal && subQuestions.length > 0 && (
        <SubQuestionsModal
          subQuestions={subQuestions}
          onClose={() => setShowSubQModal(false)}
          onNavigate={navigateToSubQuestion}
        />
      )}
      <Accordion
        expanded={expanded}
        onToggle={onExpand}
        header={header}
        data-kpi-id={item.id}
        $bg={rowBg}
        $shadow={rowShadow}
        $borderBottom="1.5px solid #dde2ea"
        $mb="15px"
      >
        <div className="kpi-slide-down">
          <KpiRowOverview
            item={item}
            analytics={analytics}
            kpiTypes={kpiTypes}
            matchingTriggers={matchingTriggers}
            triggersFallback={triggersFallback}
            projection={projection}
            emissionMode={emissionMode}
            hasPermission={hasPermission}
            canAddAndEditRecords={canAddAndEditRecords}
            subQuestions={subQuestions}
            onAdd={onAdd}
            onHistory={onHistory}
            onViewSubQuestions={() => setShowSubQModal(true)}
            lock={lock}
            accessRequested={accessRequested}
            accessDeclined={accessDeclined}
            requestingAccess={requestingAccess}
            onRequestAccess={onRequestAccess}
            canDecideAccess={canDecideAccess}
            pendingRequestIds={pendingRequestIds}
            pendingRequestLocationIds={pendingRequestLocationIds}
            onApproveAccess={onApproveAccess}
            onRejectAccess={onRejectAccess}
          />
        </div>
      </Accordion>
    </>
  );
};

export default KpiRow;
